import React, { useEffect, useState } from "react";

/**
 * LES CARNETS DE RÔLE D'UN ATELIER, à imprimer pour la séance.
 *
 * DEUX TEMPS, ET PAS UN SEUL BOUTON « générer ». Le plan ne lit que la base
 * et s'affiche instantanément ; la génération télécharge les visuels et
 * compose les PDF, soit une dizaine de secondes. Un atelier sans acteurs ou
 * dont personne n'a rédigé les arguments — le cas ordinaire d'un brouillon —
 * doit pouvoir se le voir dire tout de suite, avec sa raison, plutôt qu'après
 * une attente qui ne rendrait rien.
 *
 * LE TÉLÉCHARGEMENT PASSE PAR `fetch` ET NON PAR UN LIEN. La route est sous
 * jeton : un `<a href>` partirait sans en-tête d'autorisation et rapporterait
 * un 401 déguisé en fichier illisible.
 */
export default function NotebookPanel({ configId, token, onClose }) {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  // Ce qui est en cours de téléchargement : l'id d'un acteur, "tous", ou null.
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_API_URL}/notebooks_plan/${configId}`, {
      headers: { Authorization: "Bearer " + token },
    })
      .then((res) => res.json())
      .then((body) => {
        if (cancelled) return;
        if (body.error) setError(body.error);
        else setPlan(body);
      })
      .catch((e) => !cancelled && setError(String(e)));
    return () => { cancelled = true; };
  }, [configId, token]);

  const download = async (actors, key) => {
    setBusy(key);
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/generate_notebooks/${configId}`,
        {
          method: "POST",
          headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
          body: JSON.stringify(actors ? { actors } : {}),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Erreur ${res.status}`);
      }
      // Le nom du fichier vient du serveur : c'est lui qui sait s'il rend un
      // PDF seul ou une archive, et sous quelle convention de nommage — celle
      // des carnets BTP diffère de celle des carnets Compte-Gouttes.
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/);
      const blob = await res.blob();
      // Repli si l'en-tête n'arrive pas : l'EXTENSION suit le type reçu, pas
      // le nombre de rôles demandés. Nommer « .zip » un PDF le rendrait
      // illisible d'un double-clic.
      const fallback = blob.type === "application/pdf"
        ? `carnet_${configId}${actors ? "_" + actors[0] : ""}.pdf`
        : `carnets_${configId}.zip`;
      const filename = match ? decodeURIComponent(match[1]) : fallback;

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const ready = (plan?.actors || []).filter((a) => a.ready);

  return (
    <div className="validation-panel">
      <div className="validation-summary">
        <span>Carnets de rôle</span>
        {plan && <span className="builder-tag tag-type">{plan.kind_label}</span>}
        {plan && (
          <span className="builder-tag">
            {ready.length} carnet{ready.length !== 1 ? "s" : ""} sur{" "}
            {plan.actors.length} rôle{plan.actors.length !== 1 ? "s" : ""}
          </span>
        )}
        {plan && plan.measures > 0 && (
          <span className="builder-tag">
            {plan.measures} mesures{plan.source ? ` — ${plan.source}` : ""}
          </span>
        )}
        <button className="builder-icon-btn" onClick={onClose} title="Fermer"
                style={{ marginLeft: "auto" }}>✕</button>
      </div>

      {error && <div className="validation-finding validation-finding-error">{error}</div>}
      {!plan && !error && <div className="builder-loading">Analyse de l'atelier…</div>}

      {plan && (
        <>
          <div className="notebook-hint">
            Un carnet par rôle : sa couverture, ses mesures et l'avis qu'il défend sur
            chacune. {plan.kind_reason}.
          </div>

          {(plan.warnings || []).map((warning, i) => (
            <div key={i} className="validation-finding validation-finding-warning">{warning}</div>
          ))}

          {plan.blocking ? (
            <div className="validation-finding validation-finding-error">{plan.blocking}</div>
          ) : (
            <>
              <div className="notebook-actors">
                {plan.actors.map((actor) => (
                  <div key={actor.id}
                       className={`notebook-actor ${actor.ready ? "" : "notebook-actor-off"}`}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="notebook-actor-name">{actor.name}</div>
                      <div className="notebook-actor-meta">
                        {actor.ready
                          ? `${actor.measures} mesure${actor.measures !== 1 ? "s" : ""}`
                            + (actor.format === "tableau" ? " — tableau de synthèse" : "")
                          : actor.reason}
                      </div>
                    </div>
                    {actor.ready && (
                      <button className="builder-btn-secondary small"
                              disabled={busy !== null}
                              onClick={() => download([actor.id], actor.id)}>
                        {busy === actor.id ? "…" : "PDF"}
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button className="builder-btn-primary"
                      disabled={busy !== null}
                      onClick={() => download(null, "tous")}
                      style={{ marginTop: 12 }}>
                {busy === "tous"
                  ? "Génération en cours…"
                  : `Télécharger les ${ready.length} carnets (zip)`}
              </button>
              {busy && (
                <span className="notebook-hint" style={{ marginLeft: 10 }}>
                  Composition des PDF : une dizaine de secondes.
                </span>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
